import { useState } from "react";
import { noop } from "lodash";
import { t } from "@/i18n-js/instance";
import { useModal } from "@/react/components/Layout/ModalManagerProvider";
import { isSnippetTypeAFile } from "@/react/components/SettingsApp/CommunityBot/utilities";
import { editorRestrictions } from "@/react/constants/editorRestrictions";
import { FormTextAreaAutosize } from "@circle-react/components/shared/uikit/Form/FormTextAreaAutosize";
import { FormTipTapBlockEditor } from "@circle-react/components/shared/uikit/Form/FormTipTapBlockEditor/FormTipTapBlockEditor";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Form } from "@circle-react-shared/uikit/Form/Form";
import { Button } from "@circle-react-uikit/Button";
import { Loader } from "@circle-react-uikit/Loader";
import { Typography } from "@circle-react-uikit/Typography";
import { useBulkDeleteSnippetModal } from "../BulkDeleteSnippetModal";
import { useEditSnippet } from "../snippetsQueries";
import type { SnippetPreview } from "../snippetsQueries";
import { FileButtons } from "./FileButtons";
import { SnippetButtons } from "./SnippetButtons";

export const Content = ({
  snippet,
}: {
  snippet: SnippetPreview | undefined;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const bulkDeleteSnippetModal = useBulkDeleteSnippetModal();
  const modal = useModal();
  const editSnippetMutation = useEditSnippet({
    onSuccess: () => setIsEditing(false),
  });

  const isFile = isSnippetTypeAFile(snippet?.type ?? "");

  const handleDelete = () => {
    void bulkDeleteSnippetModal.show({
      selectedIds: [snippet?.id ?? 0],
      setSelectedIds: noop,
      onDelete: () => {
        modal.remove();
      },
    });
  };

  return (
    <div className="border-primary flex h-full min-h-0 flex-1 flex-col border-r">
      {snippet === undefined ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader />
        </div>
      ) : (
        <Form
          name="preview-snippet"
          onSubmit={data => {
            if (!isFile) {
              editSnippetMutation.mutate({
                ...data,
                id: snippet?.id,
              });
            }
          }}
          defaultValues={{
            title: snippet?.title,
            body: snippet?.body,
          }}
          className="flex h-full min-h-0 flex-1 flex-col"
          formClassName="flex flex-col min-h-0 h-full"
        >
          <div className="border-primary flex h-[72px] items-center justify-between border-b p-6">
            <div>
              <Typography.LabelLg weight="semibold" color="text-light">
                {t(
                  "settings.community_bot.knowledge.custom.preview_drawer.custom",
                )}
              </Typography.LabelLg>
              <Typography.LabelLg weight="semibold">
                {isFile
                  ? t("settings.community_bot.knowledge.custom.types.file")
                  : t("settings.community_bot.knowledge.custom.types.snippet")}
              </Typography.LabelLg>
            </div>
            {isFile ? (
              <FileButtons
                url={snippet?.file_url ?? ""}
                onDelete={() => handleDelete()}
              />
            ) : (
              <SnippetButtons
                isEditing={isEditing}
                onCancel={() => {
                  setIsEditing(false);
                }}
                onEdit={() => {
                  setIsEditing(true);
                }}
                onDelete={() => handleDelete()}
              />
            )}
          </div>
          <div
            id="scroll_this_one"
            className="h-full min-h-0 flex-1 overflow-y-scroll"
          >
            <div className="px-6 py-4">
              <FormTextAreaAutosize
                disabled={!isEditing}
                draggable={false}
                name="title"
                placeholder={t(
                  "settings.community_bot.knowledge.custom.placeholders.title",
                )}
                className="bg-primary text-dark mb-2 block w-full resize-none border-none p-0 text-2xl font-bold focus:ring-0"
                rows={1}
                onKeyPress={e => {
                  if (e.key === "Enter") e.preventDefault();
                }}
                tabIndex={0}
              />
              {snippet?.body === null ? (
                <div className="flex h-[calc(100vh-200px)] items-center justify-center">
                  <div className="flex flex-col items-center gap-4">
                    <Loader />
                    <Typography.BodySm>
                      {t(
                        "settings.community_bot.knowledge.custom.preview_drawer.still_processing",
                      )}
                    </Typography.BodySm>
                  </div>
                </div>
              ) : (
                <div className="flex h-full flex-col">
                  <div
                    className={classNames(
                      "relative",
                      snippet.truncated &&
                        "h-[calc(100vh-300px)] overflow-hidden",
                    )}
                  >
                    <FormTipTapBlockEditor
                      {...editorRestrictions}
                      placeholder={t(
                        "settings.community_bot.knowledge.custom.placeholders.body",
                      )}
                      readOnly={!isEditing}
                      name="body.body"
                      className={classNames(
                        "h-full",
                        snippet.truncated && "overflow-hidden",
                      )}
                      editorClassName="quick-post-modal--v2"
                      shouldRefetch
                    />
                    {snippet.truncated && (
                      <div className="from-primary absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t to-transparent" />
                    )}
                  </div>
                  {snippet.truncated && (
                    <div className="mt-4 flex flex-col items-center gap-4 pt-4">
                      <Typography.BodyMd weight="bold">
                        {t(
                          "settings.community_bot.knowledge.custom.preview_drawer.too_large",
                        )}
                      </Typography.BodyMd>
                      <Button
                        variant="circle"
                        onClick={() => {
                          window.open(snippet.file_url, "_blank");
                        }}
                      >
                        {t(
                          "settings.community_bot.knowledge.custom.preview_drawer.download_file",
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Form>
      )}
    </div>
  );
};
